import type { VoidFunctionComponent } from 'react';

import Card from '../generic/Card/Card';
import { useAsyncValue, useOnMount } from '../../hooks';
import { get } from '../../redux/api';
import Flex from '../generic/Flex';
import type { GetFaktoryInfoResponse } from '../../../shared/types/routes/jobs';
import Button, { BUTTON_SIZE, BUTTON_TYPE } from '../generic/Button';
import { icons } from '../generic';
import Loader from '../generic/Loader';
import { LoadingBoundary } from '../generic/LoadingBoundary';
import { errorMessagesListFromResponse, type FailedFetchResponse } from '../../helpers/errors';

const FaktoryInfo: VoidFunctionComponent = () => {
  const { setPending, setReject, setResolve, ...value } = useAsyncValue<GetFaktoryInfoResponse>();

  const fetchData = async () => {
    try {
      setPending();
      const { data } = await get<GetFaktoryInfoResponse>('/api/jobs/faktory');
      if (data) {
        setResolve(data);
      }
    } catch (err) {
      setReject(errorMessagesListFromResponse(err as FailedFetchResponse));
    }
  };

  useOnMount(fetchData);

  return (
    <Card isFullWidth>
      <Card.Header>Faktory Info</Card.Header>
      <Card.SubHeader>
        <Button type={BUTTON_TYPE.ANCHOR} size={BUTTON_SIZE.LARGE} onClick={fetchData} icon={icons.refresh}>
          Refresh
        </Button>
      </Card.SubHeader>
      <Card.Content>
        {value.errors && value.errors.map((err) => (<p>{err}</p>))}
        <Flex direction="row" justify="flex-start" align="flex-start" gap={12}>
          <Card variant="fill">
            <Card.Header>Queues:</Card.Header>
            <Card.Content>
              <LoadingBoundary
                asyncValue={value}
                renderLoading={<Loader />}
                renderValue={({ value: data }) => (
                  <>
                    {Object.entries(data.faktory.queues ?? {}).map(([key, val]) => (
                      <p>
                        - {key}: {val}
                      </p>
                    ))}
                  </>
                )}
                renderEmpty={<>No data</>}
                renderError={() => <>Error.</>}
              />
            </Card.Content>
          </Card>
          <Card variant="fill">
            <Card.Header>Tasks:</Card.Header>
            <Card.Content>
              <LoadingBoundary
                asyncValue={value}
                renderLoading={<Loader />}
                renderValue={({ value: data }) => (
                  <>
                    <p>- Busy: {data.faktory?.tasks?.Busy?.size}</p>
                    <p>- Retries: {data.faktory?.tasks?.Retries?.enqueued}</p>
                    <p>- Scheduled: {data.faktory?.tasks?.Scheduled?.enqueued}</p>
                  </>
                )}
                renderEmpty={<>No data</>}
                renderError={() => <>Error.</>}
              />
            </Card.Content>
          </Card>
          <Card variant="fill">
            <Card.Header>General Info</Card.Header>
            <Card.Content>
              <LoadingBoundary
                asyncValue={value}
                renderLoading={<Loader />}
                renderValue={({ value: data }) => (
                  <>
                    <p># of Workers: {data.faktory?.tasks?.Workers?.size}</p>
                    <p># of Connections: {data.server.connections}</p>
                    <p>Total Enqueued: {data.faktory.total_enqueued}</p>
                  </>
                )}
                renderEmpty={<>No data</>}
                renderError={() => <>Error.</>}
              />
            </Card.Content>
          </Card>
        </Flex>
      </Card.Content>
    </Card>
  );
};

export default FaktoryInfo;
